import React, { useEffect, useState } from 'react';

import { CircularProgress, IconButton, LinearProgress, Typography } from '@mui/material';
import type { GridColDef, GridRowSelectionModel, GridToolbarProps } from '@mui/x-data-grid-premium';
import { DataGridPremium, GridOverlay } from '@mui/x-data-grid-premium';
import { Database, Plus, Trash2 } from 'lucide-react';
import {
  AlineButton,
  AlineCheckbox,
  AlineTooltip,
  EmptyState,
  FiltersToolbarButton,
  ReportFiltersPanel,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { getSingleSelectOperatorsWithPreservedSearch } from 'src/components/generic/data-grid/single-select-grid-filter-operators';
import { dateValueGetter } from 'src/components/reports/helpers';
import * as Apollo from 'src/gql/generated/apollo-types';
import { useDataGridSearch } from 'src/hooks';
import {
  createDataGridPremiumFooterlessPaperStyles,
  FiltersAnchorProvider,
  useFiltersAnchorState,
} from 'src/hooks/data-grid-premium';
import { makeStyles } from 'tss-react/mui';

import {
  getDisplayImportFailedReason,
  getDisplayImportStatus,
  getIsCommittedActiveImport,
} from './data-migration-status';

type ImportRow = Apollo.DocumentMetadataImportHistoryFieldsFragment;

interface DataMigrationHistoryGridProps {
  deleting: boolean;
  hasRunningImport: boolean;
  imports: ImportRow[];
  initialLoading: boolean;
  onDelete: () => void;
  onNewImport: () => void;
  onOpenImport: (importId: string) => void;
  onSelectionModelChange: (model: GridRowSelectionModel) => void;
  selectedCount: number;
  selectionModel: GridRowSelectionModel;
}

interface DataMigrationStatusChipProps {
  importRecord: ImportRow;
}

interface DataMigrationStatusCellProps {
  importRecord: ImportRow;
}

interface DataMigrationActionCellProps {
  importRecord: ImportRow;
  onOpenImport: (importId: string) => void;
}

interface DataMigrationToolbarProps extends GridToolbarProps {
  deleting?: boolean;
  hasRunningImport?: boolean;
  onDelete?: () => void;
  onNewImport?: () => void;
  selectedCount?: number;
}

const DATA_MIGRATION_EMPTY_SUBTITLE =
  'Download an Excel template, map Aline properties to your spreadsheet columns, and bulk-update document metadata across your repository in one guided import.';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    '&&': {
      fontSize: 12,
      width: 110,
    },
  },
  actionCell: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'flex-end',
    width: '100%',
  },
  emptyIcon: {
    color: '#146EE8',
  },
  emptySubtitle: {
    maxWidth: 480,
  },
  progressBar: {
    height: 6,
    width: '100%',
  },
  progressHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  progressLabel: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  progressText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  progressTextContainer: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    maxWidth: 220,
    width: '100%',
  },
  statusCell: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    height: '100%',
    width: '100%',
  },
}));

const STATUS_CONFIG: Record<
  Apollo.DocumentMetadataImportStatus,
  { bg: string; label: string; text: string }
> = {
  [Apollo.DocumentMetadataImportStatus.Canceled]: {
    bg: '#FCEBEB',
    label: 'Cancelled',
    text: '#DE3B3B',
  },
  [Apollo.DocumentMetadataImportStatus.Completed]: {
    bg: '#12B0662E',
    label: 'Completed',
    text: '#12B066',
  },
  [Apollo.DocumentMetadataImportStatus.CompletedWithErrors]: {
    bg: '#12B0662E',
    label: 'Completed',
    text: '#12B066',
  },
  [Apollo.DocumentMetadataImportStatus.Draft]: { bg: '#E0E0E0', label: 'Draft', text: '#616161' },
  [Apollo.DocumentMetadataImportStatus.Failed]: { bg: '#FCEBEB', label: 'Failed', text: '#DE3B3B' },
  [Apollo.DocumentMetadataImportStatus.Processing]: {
    bg: '#146EE81A',
    label: 'Running',
    text: '#146EE8',
  },
  [Apollo.DocumentMetadataImportStatus.ReadyToImport]: {
    bg: '#E0E0E0',
    label: 'Ready',
    text: '#616161',
  },
  [Apollo.DocumentMetadataImportStatus.Uploaded]: {
    bg: '#146EE81A',
    label: 'Running',
    text: '#146EE8',
  },
  [Apollo.DocumentMetadataImportStatus.Validating]: {
    bg: '#146EE81A',
    label: 'Loading...',
    text: '#146EE8',
  },
};

const PASSIVE_UPLOADED_STATUS_CONFIG = {
  bg: '#E0E0E0',
  label: 'Uploaded',
  text: '#616161',
};

const PASSIVE_VALIDATING_STATUS_CONFIG = {
  bg: '#E0E0E0',
  label: 'Validating',
  text: '#616161',
};

interface DataMigrationGridEmptyProps {
  onNewImport: () => void;
}

function DataMigrationGridEmpty({ onNewImport }: DataMigrationGridEmptyProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <GridOverlay>
      <EmptyState
        buttonProps={{ onClick: onNewImport }}
        buttonText="Import Data"
        icon={<Database className={classes.emptyIcon} size={64} strokeWidth={1.25} />}
        subtitle={DATA_MIGRATION_EMPTY_SUBTITLE}
        subtitleClassName={classes.emptySubtitle}
        title="No data imported yet"
      />
    </GridOverlay>
  );
}

const STATUS_VALUE_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, status]) => ({
  label: status.label,
  value,
}));

function getColumns(onOpenImport: (importId: string) => void): GridColDef<ImportRow>[] {
  return [
    {
      field: 'displayName',
      filterable: true,
      flex: 1.5,
      headerName: 'Name',
    },
    {
      field: 'createdAt',
      filterable: true,
      flex: 0.5,
      headerName: 'Date',
      type: 'date',
      valueFormatter: (value: Date | null) => value?.toLocaleDateString() ?? '',
      valueGetter: dateValueGetter,
    },
    {
      field: 'status',
      filterOperators: getSingleSelectOperatorsWithPreservedSearch(),
      filterable: true,
      flex: 1.2,
      headerName: 'Status',
      minWidth: 240,
      renderCell: params => <DataMigrationStatusCell importRecord={params.row} />,
      type: 'singleSelect',
      valueFormatter: getDocumentMetadataImportStatusLabel,
      valueOptions: STATUS_VALUE_OPTIONS,
    },
    {
      align: 'right',
      field: 'actions',
      filterable: false,
      headerAlign: 'right',
      // No header name on purpose...
      headerName: '',
      minWidth: 160,
      renderCell: params => (
        <DataMigrationActionCell importRecord={params.row} onOpenImport={onOpenImport} />
      ),
      sortable: false,
    },
  ];
}

function getDocumentMetadataImportStatusLabel(value: unknown): string {
  if (!getIsDocumentMetadataImportStatus(value)) return String(value);

  return STATUS_CONFIG[value].label;
}

function DataMigrationStatusChip({ importRecord }: DataMigrationStatusChipProps): JSX.Element {
  const statusConfig = getImportStatusConfig(importRecord);
  const failedReason = getDisplayImportFailedReason(importRecord);

  return (
    <AlineTooltip arrow placement="top" title={failedReason}>
      <div
        style={{
          backgroundColor: statusConfig.bg,
          borderRadius: 3,
          padding: '4px 0',
          textAlign: 'center',
          width: 90,
        }}
      >
        <Typography sx={{ color: statusConfig.text, fontSize: 10, fontWeight: 600 }}>
          {statusConfig.label}
        </Typography>
      </div>
    </AlineTooltip>
  );
}

function getImportStatusConfig(importRecord: ImportRow) {
  const displayStatus = getDisplayImportStatus(importRecord);
  const isCommittedActiveImport = getIsCommittedActiveImport(importRecord);

  if (displayStatus === Apollo.DocumentMetadataImportStatus.Uploaded && !isCommittedActiveImport) {
    return PASSIVE_UPLOADED_STATUS_CONFIG;
  }

  if (
    displayStatus === Apollo.DocumentMetadataImportStatus.Validating &&
    !isCommittedActiveImport
  ) {
    return PASSIVE_VALIDATING_STATUS_CONFIG;
  }

  return STATUS_CONFIG[displayStatus];
}

function DataMigrationStatusCell({ importRecord }: DataMigrationStatusCellProps): JSX.Element {
  const { classes } = useStyles();
  const displayStatus = getDisplayImportStatus(importRecord);

  if (displayStatus === Apollo.DocumentMetadataImportStatus.Processing) {
    return (
      <div className={classes.statusCell}>
        <div className={classes.progressTextContainer}>
          <div className={classes.progressHeader}>
            <div className={classes.progressLabel}>
              <Typography noWrap className={classes.progressText} variant="body2">
                Running
              </Typography>
              <CircularProgress size={12} thickness={5} />
            </div>
            <Typography noWrap className={classes.progressText} variant="body2">
              {getImportProgressText(importRecord)}
            </Typography>
          </div>
          <LinearProgress
            className={classes.progressBar}
            value={getImportProgressPercentage(importRecord)}
            variant="determinate"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.statusCell}>
      <DataMigrationStatusChip importRecord={importRecord} />
    </div>
  );
}

function DataMigrationActionCell({
  importRecord,
  onOpenImport,
}: DataMigrationActionCellProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.actionCell}>
      <AlineButton
        buttonClassName={classes.actionButton}
        color="primary"
        variant="outlined"
        onClick={event => {
          event.stopPropagation();
          onOpenImport(importRecord.id);
        }}
        onMouseDown={event => event.stopPropagation()}
      >
        {getOpenImportCta(getDisplayImportStatus(importRecord))}
      </AlineButton>
    </div>
  );
}

function getIsDocumentMetadataImportStatus(
  value: unknown
): value is Apollo.DocumentMetadataImportStatus {
  const isKnownStatus = typeof value === 'string' && value in STATUS_CONFIG;

  return isKnownStatus;
}

function DataMigrationToolbar({
  deleting,
  hasRunningImport = false,
  onDelete,
  onNewImport,
  selectedCount = 0,
}: DataMigrationToolbarProps): JSX.Element {
  const { classes: toolbarClasses } = useListToolbarStyles();
  const { search, setSearch } = useDataGridSearch();
  const hasSelection = selectedCount > 0;

  return (
    <div className={toolbarClasses.toolbar}>
      <FiltersToolbarButton />
      <Spacer />
      {hasSelection && (
        <>
          <AlineTooltip arrow placement="top" title="Delete data migrations">
            <IconButton disabled={deleting} onClick={onDelete}>
              <Trash2 size={20} />
            </IconButton>
          </AlineTooltip>
        </>
      )}
      <SearchInput placeholder="Search data migrations..." search={search} setSearch={setSearch} />
      <AlineTooltip
        arrow
        placement="top"
        title={
          hasRunningImport
            ? 'A data migration is running or loading. Wait for it to finish before starting a new one.'
            : ''
        }
      >
        <span>
          <AlineButton
            color="primary"
            disabled={hasRunningImport}
            endIcon={<Plus size={16} />}
            variant="contained"
            onClick={onNewImport}
          >
            New Data Migration
          </AlineButton>
        </span>
      </AlineTooltip>
    </div>
  );
}

export function DataMigrationHistoryGrid({
  deleting,
  hasRunningImport,
  imports,
  initialLoading,
  onDelete,
  onNewImport,
  onOpenImport,
  onSelectionModelChange,
  selectedCount,
  selectionModel,
}: DataMigrationHistoryGridProps): JSX.Element {
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const [, setEtaRefreshTick] = useState(0);
  const columns = React.useMemo(() => getColumns(onOpenImport), [onOpenImport]);
  const DataMigrationGridEmptyOverlay = React.useCallback(
    function DataMigrationGridEmptyOverlay() {
      return <DataMigrationGridEmpty onNewImport={onNewImport} />;
    },
    [onNewImport]
  );
  const hasRunningImports = imports.some(importRecord => getIsCommittedActiveImport(importRecord));

  useEffect(() => {
    if (!hasRunningImports) return;

    const intervalId = window.setInterval(() => {
      setEtaRefreshTick(tick => tick + 1);
    }, 5_000);

    return () => window.clearInterval(intervalId);
  }, [hasRunningImports]);

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <DataGridPremium
        checkboxSelection
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        hideFooter
        showToolbar
        columns={columns}
        getRowId={row => row.id}
        loading={initialLoading}
        rowSelectionModel={selectionModel}
        rows={imports}
        slotProps={{
          filterPanel: {
            filterableColumns: 'visible',
          },
          loadingOverlay: {
            noRowsVariant: 'linear-progress',
            variant: 'linear-progress',
          },
          panel: {
            placement: 'bottom-start',
            target: filtersAnchorEl ?? undefined,
          },
          toolbar: {
            deleting,
            hasRunningImport,
            onDelete,
            onNewImport,
            selectedCount,
          },
        }}
        slots={{
          baseCheckbox: AlineCheckbox,
          filterPanel: ReportFiltersPanel,
          noRowsOverlay: DataMigrationGridEmptyOverlay,
          toolbar: DataMigrationToolbar,
        }}
        sx={createDataGridPremiumFooterlessPaperStyles}
        onRowSelectionModelChange={onSelectionModelChange}
      />
    </FiltersAnchorProvider>
  );
}

function getOpenImportCta(status: Apollo.DocumentMetadataImportStatus): string {
  if (status === Apollo.DocumentMetadataImportStatus.Draft) return 'Continue';
  if (status === Apollo.DocumentMetadataImportStatus.ReadyToImport) return 'Finish';

  return 'View';
}

function getImportProgressText(importRecord: ImportRow): string {
  const totalRows = importRecord.summary?.totalRows ?? 0;
  const processedRows = Math.min(importRecord.summary?.processedRowCount ?? 0, totalRows);

  return `${processedRows}/${totalRows} rows`;
}

function getImportProgressPercentage(importRecord: ImportRow): number {
  const totalRows = importRecord.summary?.totalRows ?? 0;
  if (totalRows <= 0) return 0;

  const processedRows = Math.min(importRecord.summary?.processedRowCount ?? 0, totalRows);
  return (processedRows / totalRows) * 100;
}
